import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaLock, FaSpinner, FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import PublicLayout from '../../components/PublicLayout';
import '../../styles/Auth.css';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true = valid, false = invalid
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

  // Check if token is valid on component mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    
    // Basic token format validation (should be 64-character hex string)
    if (token.length !== 64 || !/^[a-f0-9]+$/i.test(token)) {
      setTokenValid(false);
      return;
    }
    
    setTokenValid(true);
  }, [token]);

  const handleShowToast = (message, type = 'error') => {
    setToast({ open: true, message, type });
    setTimeout(() => {
      setToast({ open: false, message: '', type: 'error' });
    }, 5000);
  };

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (password.length > 100) {
      return 'Password must be less than 100 characters';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    const passwordError = validatePassword(password);
    if (passwordError) {
      handleShowToast(passwordError);
      return;
    }
    
    if (password !== confirmPassword) {
      handleShowToast('Passwords do not match');
      return;
    }

    setLoading(true);
    
    try {
      const API_URL = process.env.NODE_ENV === 'development' 
        ? '/api' 
        : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
      
      const response = await fetch(`${API_URL}/auth/reset-password/${token}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        if (response.status === 400) {
          handleShowToast('Invalid or expired reset token. Please request a new password reset.');
        } else if (response.status === 503) {
          handleShowToast('Service temporarily unavailable. Please try again later.');
        } else {
          handleShowToast('An error occurred. Please try again.');
        }
        return;
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setPasswordReset(true);
      } else {
        handleShowToast(data.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      console.error('Password reset failed:', err);
      handleShowToast('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Token is invalid or missing
  if (tokenValid === false) {
    return (
      <PublicLayout>
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                margin: '0 auto 20px',
                fontSize: '32px',
                color: '#ef4444'
              }}>
                ⚠️
              </div>
              <h1>Invalid Reset Link</h1>
              <p>The password reset link is invalid or has expired</p>
            </div>
            
            <div style={{ 
              backgroundColor: '#fef3c7', 
              border: '1px solid #fbbf24', 
              borderRadius: '8px', 
              padding: '20px', 
              margin: '20px 0',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#92400e', margin: '0 0 10px 0', fontSize: '16px' }}>
                What to do next:
              </h3>
              <p style={{ color: '#374151', margin: '0', fontSize: '14px', lineHeight: '1.5' }}>
                Password reset links expire after 10 minutes for security.<br/>
                Please request a new password reset link.
              </p>
            </div>

            <div className="auth-footer">
              <p>
                <Link to="/forgot-password" style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  color: '#3b9efc', 
                  textDecoration: 'none',
                  fontSize: '14px'
                }}>
                  Request New Reset Link
                </Link>
              </p>
              <p style={{ marginTop: '15px' }}>
                <Link to="/login" style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  color: '#6b7280', 
                  textDecoration: 'none',
                  fontSize: '14px'
                }}>
                  <FaArrowLeft style={{ marginRight: '8px' }} />
                  Back to Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Password successfully reset
  if (passwordReset) {
    return (
      <PublicLayout>
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                backgroundColor: '#d1fae5',
                borderRadius: '50%',
                margin: '0 auto 20px',
                fontSize: '32px',
                color: '#10b981'
              }}>
                ✓
              </div>
              <h1>Password Reset Successful</h1>
              <p>Your password has been successfully updated</p>
            </div>
            
            <div style={{ 
              backgroundColor: '#f0f9ff', 
              border: '1px solid #3b9efc', 
              borderRadius: '8px', 
              padding: '20px', 
              margin: '20px 0',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#1e40af', margin: '0 0 10px 0', fontSize: '16px' }}>
                You can now log in with your new password
              </h3>
              <p style={{ color: '#374151', margin: '0', fontSize: '14px', lineHeight: '1.5' }}>
                Your account is secure and ready to use.
              </p>
            </div>

            <div className="auth-footer">
              <button
                onClick={() => navigate('/login')}
                style={{
                  backgroundColor: '#3b9efc',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  width: '100%',
                  marginBottom: '15px'
                }}
              >
                Continue to Login
              </button>
              <p style={{ marginTop: '15px', fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
                For security reasons, you'll need to log in again with your new password.
              </p>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Show loading while checking token validity
  if (tokenValid === null) {
    return (
      <PublicLayout>
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px', color: '#3b9efc' }} />
              </div>
              <h1>Verifying Reset Link</h1>
              <p>Please wait while we verify your password reset link...</p>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Main reset password form
  return (
    <PublicLayout>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Create New Password</h1>
            <p>Enter your new password below</p>
          </div>
          
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <div className="input-with-icon">
                <FaLock className="input-icon" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  aria-invalid={toast.open ? 'true' : 'false'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: '16px',
                    padding: '4px'
                  }}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="input-with-icon">
                <FaLock className="input-icon" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  aria-invalid={toast.open ? 'true' : 'false'}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: '16px',
                    padding: '4px'
                  }}
                  title={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div style={{ 
              backgroundColor: '#fef3c7', 
              border: '1px solid #fbbf24', 
              borderRadius: '8px', 
              padding: '15px', 
              margin: '20px 0'
            }}>
              <p style={{ color: '#92400e', margin: '0', fontSize: '14px', textAlign: 'center' }}>
                <strong>Password Requirements:</strong> At least 6 characters long
              </p>
            </div>
            
            <button 
              type="submit"
              className="auth-button"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '16px' }} />
                  Updating Password...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
          
          <div className="auth-footer">
            <p>
              <Link to="/login" style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                color: '#3b9efc', 
                textDecoration: 'none',
                fontSize: '14px'
              }}>
                <FaArrowLeft style={{ marginRight: '8px' }} />
                Back to Login
              </Link>
            </p>
          </div>

          {/* Toast Messages */}
          {toast.open && (
            <div 
              className="login-toast-error" 
              style={{ 
                border: toast.type === 'success' ? '1px solid #10b981' : '1px solid #e53935', 
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)', 
                alignItems: 'center', 
                borderRadius: '8px', 
                position: 'relative',
                backgroundColor: toast.type === 'success' ? '#f0fdf4' : '#fef2f2'
              }}
            >
              <button 
                onClick={() => setToast({ open: false, message: '', type: 'error' })}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: toast.type === 'success' ? '#10b981' : '#e53935',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                ×
              </button>
              <div className="login-toast-content">
                <div 
                  className="login-toast-title" 
                  style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    marginBottom: '2px', 
                    color: '#222', 
                    paddingLeft: '10px'
                  }}
                >
                  {toast.type === 'success' ? ' Success' : '❗ Error'}
                </div>
                <div 
                  className="login-toast-message" 
                  style={{ 
                    fontSize: '0.75rem', 
                    color: toast.type === 'success' ? '#10b981' : '#e53935', 
                    paddingLeft: '10px', 
                    paddingRight: '24px'
                  }}
                >
                  {toast.message}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
